import { FormProvider, useForm } from 'react-hook-form'
import { Box, Button } from '@mui/material'
import { i18nTranslationHelper } from '@/i18n'
import PhoneField from '../input/phone-field/phone-field'
import ModalHeader from './modal-header'

/** Coletar é para quem não tem telefone; confirmar, para quem tem mas ainda não verificou. */
export type PhoneFlowStep = 'collect-phone' | 'confirm-phone'

type PhoneFormValues = {
  phone: string
}

type PhoneStepProps = {
  step: PhoneFlowStep
  defaultPhone?: string
  onSubmit: (phone: string) => Promise<void>
  isLoading: boolean
}

const PHONE_STEP_I18N: Record<PhoneFlowStep, { titleKey: string; descriptionKey: string }> = {
  'collect-phone': {
    titleKey: 'careerMentorFlowModal.collectPhone.title',
    descriptionKey: 'careerMentorFlowModal.collectPhone.description'
  },
  'confirm-phone': {
    titleKey: 'careerMentorFlowModal.confirmPhone.title',
    descriptionKey: 'careerMentorFlowModal.confirmPhone.description'
  }
}

export const isPhoneStep = (step: string): step is PhoneFlowStep =>
  step === 'collect-phone' || step === 'confirm-phone'

/**
 * A etapa de telefone dos fluxos de mensagem ativa.
 *
 * Sem telefone verificado o backend não tem para onde mandar a mensagem, então todo fluxo que
 * dispara uma precisa passar por aqui antes. Extraído do modal da mentoria de carreira para o
 * convite do app reusar em vez de repetir.
 */
export default function PhoneStep({
  step,
  defaultPhone = '',
  onSubmit,
  isLoading
}: Readonly<PhoneStepProps>) {
  const t = i18nTranslationHelper.getTComponents
  const { titleKey, descriptionKey } = PHONE_STEP_I18N[step]

  const methods = useForm<PhoneFormValues>({
    mode: 'onChange',
    defaultValues: { phone: defaultPhone }
  })

  const phoneValue = methods.watch('phone')
  const isInvalidPhone = !phoneValue || !!methods.formState.errors?.phone

  const handleSubmit = methods.handleSubmit(async ({ phone }) => {
    await onSubmit(phone)
  })

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={3}>
        <ModalHeader titleKey={titleKey} descriptionKey={descriptionKey} />

        <PhoneField
          name="phone"
          required
          label={t('careerMentorFlowModal.phoneLabel')}
          size="medium"
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={isLoading || isInvalidPhone}
        >
          {t('careerMentorFlowModal.advanceCta')}
        </Button>
      </Box>
    </FormProvider>
  )
}
